import styled from 'styled-components'
import { tabletBreakpoint, desktopBreakpoint } from 'react-components'
import { SkeletonBox } from '../../../common/card-skeletons'

export const HeaderContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
`

export const TopRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
`

export const CountTitle = styled.h2`
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1.5rem;
    display: block;
    color: #282828;
    overflow: visible;
    text-overflow: clip;
    flex: 1;
    max-width: auto;

    @media only screen and (min-width: ${tabletBreakpoint.max}px) {
        font-size: 1.5625rem;
        line-height: 1.875rem;
    }
`

export const MapLink = styled.a`
    color: #006ad4;
    font-weight: 400;
    font-size: 0.875rem;
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;
`

export const ViewToggleContainer = styled.div<{ $isReverse?: boolean }>`
    display: none;
    background: #f6f6f6;
    border-radius: 32px;
    padding: 2px;
    margin-left: 0.5rem;

    @media only screen and (min-width: ${tabletBreakpoint.min}px) {
        display: flex;
        flex-direction: ${({ $isReverse }) => ($isReverse ? 'row-reverse' : 'row')};
    }
`

export const ToggleButton = styled.button<{ isActive: boolean }>`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.25rem 0.75rem;
    border-radius: 32px;
    background: ${({ isActive, theme }) => (isActive ? theme.colors.white : 'transparent')};
    color: ${({ theme }) => theme.colors.tfwBlack99};
    font-weight: 600;
    border: 2px solid ${({ isActive }) => (isActive ? '#282828' : 'transparent')};
    cursor: pointer;
    font-size: 0.875rem;
    line-height: 1.375rem;
    transition: all 0.2s ease-in-out;

    &:hover {
        background: ${({ isActive, theme }) => (isActive ? theme.colors.white : 'rgba(0,0,0,0.05)')};
    }
`

export const ControlsRow = styled.div`
    display: none;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;

    @media only screen and (min-width: ${desktopBreakpoint.min}px) {
        display: flex;
    }
`

export const DottedSeparator = styled.div`
    width: 1px;
    height: 24px;
    border-left: 1px dotted #ccc;
`

export const ClearAllLink = styled.a`
    font-family: ${props => props.theme.typography.font.family.latoVerdana};
    color: #006ad4;
    font-weight: 400;
    font-size: 0.875rem;
    line-height: 1.375rem;
    text-decoration: none;
    cursor: pointer;
    margin-left: 0.5rem;
    white-space: nowrap;
    flex-shrink: 0;

    &:hover {
        text-decoration: underline;
    }
`

export const ActiveFilterChip = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 2px 6px;
    background: white;
    border: 2px solid ${({ theme }) => theme.colors.tfwBlack99};
    border-radius: 128px;
    font-size: 0.875rem;
    font-weight: 300;
    cursor: pointer;

    &:hover {
        background: ${({ theme }) => theme.colors.tfwGrey};
    }
`

export const SortWrapper = styled.div`
    span[class*='Header'] {
        height: 2.5rem;
        padding: 0.5rem 1rem;
        background-color: #ffffffff;
        border: 1px solid #d5d5d5;
        border-radius: 128px;
        cursor: pointer;

        &:hover {
            border-color: #a0a0a0;
            background-color: #ebebeb;
        }
    }
`

export const TitleSkeleton = styled.div`
    height: 1.5rem;
    width: 12rem;
    border-radius: 0.25rem;
    flex: 1;
    max-width: 16rem;
    background-color: #e0e0e0;

    @media only screen and (min-width: ${tabletBreakpoint.max}px) {
        height: 1.875rem;
        width: 16rem;
    }
`

export const BannerContainer = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
    background: ${({ theme }) => theme.colors.tfwBlueLighter};
    border-radius: 0.75rem;
    padding: 0.75rem 1rem;
    width: 100%;
    box-sizing: border-box;

    @media only screen and (min-width: ${tabletBreakpoint.max}px) {
        align-items: center;
    }
`

export const BannerLeft = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    flex: 1;

    @media only screen and (min-width: ${tabletBreakpoint.max}px) {
        align-items: center;
        gap: 0.75rem;
        flex: unset;
        flex-shrink: 0;
    }
`

export const ThunderIconCircle = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.white};
    flex-shrink: 0;
`

export const BannerTextGroup = styled.div`
    display: flex;
    flex-direction: column;
`

export const BannerLabel = styled.span`
    font-size: 0.625rem;
    line-height: 0.75rem;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.tfwHyperLink};
    white-space: nowrap;
`

export const BannerDescription = styled.p`
    font-size: 0.875rem;
    line-height: 1.375rem;
    color: ${({ theme }) => theme.colors.tfwBlack99};
    font-weight: 400;
    margin: 0;

    @media only screen and (min-width: ${tabletBreakpoint.max}px) {
        font-size: 1rem;
        line-height: 1.5rem;
    }
`

export const ToggleSwitchButton = styled.button<{ $isChecked: boolean }>`
    position: relative;
    width: 2.5rem;
    height: 1.5rem;
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
    flex-shrink: 0;
    border-radius: 1rem;

    &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.colors.tfwHyperLink};
        outline-offset: 2px;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
`

export const ToggleSwitchTrack = styled.div<{ $isChecked: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    width: 2.5rem;
    height: 1.5rem;
    border-radius: 1rem;
    background: ${({ $isChecked, theme }) =>
        $isChecked ? theme.colors.tfwHyperLink : theme.colors.tfwDefaultInputBorder};
    transition: background 0.2s ease-in-out;
`

export const ToggleSwitchThumb = styled.div<{ $isChecked: boolean }>`
    position: absolute;
    top: 0.25rem;
    left: ${({ $isChecked }) => ($isChecked ? '1.25rem' : '0.25rem')};
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.white};
    transition: left 0.2s ease-in-out;
`

export const BannerSkeleton = styled(SkeletonBox)`
    height: 3rem;
    border-radius: 0.75rem;
    width: 100%;
`
